import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useMe } from '../api/me';
import { useCreateJob, useCreateInteractiveJob } from '../api/jobs';
import { Modal } from './Modal';
import { TextField, TextAreaField, SelectField } from './FormField';
import { RepoCombobox } from './RepoCombobox';
import { ActionButton } from './ActionButton';
import { InlineError } from './InlineError';
import { useToast } from './toastContext';
import { normalizeRepo, isOwnerRepo } from '../lib/repo';
import { DEFAULT_REPO } from '../data/orgRepos';
import type { ButlerModel, CreateJobRequest } from '../types';

// Values are the a1ec ButlerModel aliases; agent-run resolves each alias to a
// specific model version, which the labels name. Opus 5.5 is the default.
const DEFAULT_MODEL: ButlerModel = 'opus';
const MODEL_OPTIONS: ReadonlyArray<{ value: ButlerModel; label: string }> = [
  { value: 'opus', label: 'Opus 5.5 (default)' },
  { value: 'sonnet', label: 'Sonnet 5.5' },
  { value: 'fable', label: 'Fable 5.1' },
  { value: 'haiku', label: 'Haiku 4.5' },
];

type JobKind = 'PROMPT' | 'INTERACTIVE';

// Editable default like "sandbox-0710-1432" (local clock). Read once in a
// useState initializer (React 19 purity rule) — see RelativeTime.tsx.
function defaultSessionName(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `sandbox-${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`;
}

// New-job modal (A1EE-338 + A1EE-575). Opens from the global Header. A
// Prompt/Interactive toggle switches between a prompt-mode job (implicit
// autoStart) and an interactive session (create, then optionally StartJob).
export function NewJobModal({
  open,
  onClose,
  initialKind = 'PROMPT',
}: {
  open: boolean;
  onClose: () => void;
  // Which tab the modal opens on. The Header's "New job" leaves it PROMPT; the
  // jobs-list "New session" card opens it on INTERACTIVE. Only the initial value —
  // the user can still toggle. Callers that need a specific tab should mount a
  // fresh instance (conditional render) so this initializer runs.
  initialKind?: JobKind;
}): ReactElement {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const createJob = useCreateJob();
  const createInteractive = useCreateInteractiveJob();

  const [kind, setKind] = useState<JobKind>(initialKind);
  const [repo, setRepo] = useState(DEFAULT_REPO);
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState<ButlerModel>(DEFAULT_MODEL);
  const [branch, setBranch] = useState('');
  const [warmpool, setWarmpool] = useState('');
  const [debug, setDebug] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [name, setName] = useState(() => defaultSessionName(new Date()));
  const [autostart, setAutostart] = useState(true);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // The prompt is the state worth guarding: it's authored text, unlike the
  // pick-a-value fields (repo/model/branch/name), which cost nothing to redo.
  // Since callers mount this component fresh per open, closing really does
  // discard the draft — hence the confirm below (requested on #117).
  const dirty = prompt.trim().length > 0;

  // Guarded close for every user-initiated close path — Cancel, the header
  // Close button, the backdrop, and Esc all funnel through the Modal's
  // onClose. Post-create closes (the mutation onSuccess handlers) call
  // onClose directly and stay unguarded. While the confirm dialog is up this
  // is a no-op: both Modals' capture-phase Esc listeners fire on the same
  // keydown (stopPropagation doesn't stop same-target listeners), so without
  // the early return the confirm would re-arm itself as it closes.
  const requestClose = () => {
    if (confirmDiscardOpen) return;
    if (dirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  // Hard navigations (tab close, reload, typing a URL) can't show the in-app
  // confirm — arm the browser-native beforeunload prompt while a draft exists.
  useEffect(() => {
    if (!open || !dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome <119 ignores preventDefault and keys off returnValue instead.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [open, dirty]);

  const submitPrompt = () => {
    const normalizedRepo = normalizeRepo(repo);
    const repoOk = isOwnerRepo(normalizedRepo);
    const promptOk = prompt.trim().length > 0;
    setRepoError(repoOk ? null : 'Repo must be in owner/name form (e.g. attentive-mobile/code).');
    setPromptError(promptOk ? null : 'Prompt is required.');
    if (!repoOk || !promptOk || !me) return;

    const request: CreateJobRequest = {
      userId: me.id,
      repo: normalizedRepo,
      prompt: prompt.trim(),
      autoStart: true,
      debug,
    };
    request.model = model;
    if (branch.trim()) request.branch = branch.trim();
    if (warmpool.trim()) request.warmpool = warmpool.trim();

    createJob.mutate(request, {
      onSuccess: (data) => {
        if (data.runId) {
          showToast('Job created', 'success');
          onClose();
          void navigate(`/jobs/${data.job.id}/runs/${data.runId}`);
        } else {
          showToast(data.dispatchError?.summary ?? 'Job created but dispatch failed.', 'error');
          onClose();
          void navigate(`/jobs/${data.job.id}`);
        }
      },
    });
  };

  const submitInteractive = () => {
    const normalizedRepo = normalizeRepo(repo);
    const repoOk = normalizedRepo === '' || isOwnerRepo(normalizedRepo);
    setRepoError(repoOk ? null : 'Repo must be in owner/name form (e.g. attentive-mobile/code).');
    setPromptError(null);
    if (!repoOk || !me) return;

    createInteractive.mutate(
      {
        userId: me.id,
        name: name.trim() || undefined,
        repo: normalizedRepo || undefined,
        autostart,
      },
      {
        onSuccess: (data) => {
          if (data.runId) {
            showToast('Session started', 'success');
            onClose();
            void navigate(`/jobs/${data.job.id}/runs/${data.runId}`);
          } else {
            if (autostart) {
              showToast(
                data.dispatchError?.summary ?? 'Session created but start failed.',
                'error',
              );
            } else {
              showToast('Session created', 'success');
            }
            onClose();
            void navigate(`/jobs/${data.job.id}`);
          }
        },
      },
    );
  };

  const submit = () => (kind === 'INTERACTIVE' ? submitInteractive() : submitPrompt());

  const interactive = kind === 'INTERACTIVE';
  const activeError = interactive ? createInteractive.error : createJob.error;
  const activeIsError = interactive ? createInteractive.isError : createJob.isError;
  const pending = interactive ? createInteractive.isPending : createJob.isPending;
  const submitLabel = interactive ? (autostart ? 'Start session' : 'Create session') : 'Create job';
  const pendingLabel = interactive ? (autostart ? 'Starting…' : 'Creating…') : 'Creating…';

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        title="New job"
        footer={
          <>
            <ActionButton onClick={requestClose} variant="neutral">
              Cancel
            </ActionButton>
            <ActionButton
              onClick={submit}
              variant="primary"
              pending={pending}
              pendingLabel={pendingLabel}
              disabled={!me}
            >
              {submitLabel}
            </ActionButton>
          </>
        }
      >
        <div className="space-y-4">
          {activeIsError && <InlineError heading="Couldn't create job" error={activeError} />}

          <div className="flex gap-2" role="group" aria-label="Job type">
            {(['PROMPT', 'INTERACTIVE'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                aria-pressed={kind === k}
                className={
                  'rounded border px-3 py-1.5 text-sm font-semibold ' +
                  (kind === k
                    ? 'border-sky-600 bg-sky-50 text-sky-800'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400')
                }
              >
                {k === 'PROMPT' ? 'Prompt' : 'Interactive'}
              </button>
            ))}
          </div>

          {interactive ? (
            <>
              <TextField
                id="new-job-name"
                label="Name"
                value={name}
                onChange={setName}
                placeholder="a name to recognize this sandbox"
              />
              <RepoCombobox
                id="new-job-repo"
                value={repo}
                onChange={setRepo}
                error={repoError}
                required={false}
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={autostart}
                  onChange={(e) => setAutostart(e.target.checked)}
                />
                Autostart (start the sandbox now)
              </label>
            </>
          ) : (
            <>
              <RepoCombobox id="new-job-repo" value={repo} onChange={setRepo} error={repoError} />
              <TextAreaField
                id="new-job-prompt"
                label="Prompt"
                value={prompt}
                onChange={setPrompt}
                error={promptError}
                required
                rows={5}
                placeholder="Describe the task for the agent…"
              />
              <SelectField
                id="new-job-model"
                label="Model"
                value={model}
                onChange={(value) => setModel(value as ButlerModel)}
                options={MODEL_OPTIONS}
              />
              <TextField
                id="new-job-branch"
                label="Branch (optional)"
                value={branch}
                onChange={setBranch}
                placeholder="defaults to the repo's default branch"
              />
              <div className="border-t border-slate-200 pt-3">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800"
                  aria-expanded={advancedOpen}
                >
                  {advancedOpen ? '▾' : '▸'} Advanced
                </button>
                {advancedOpen && (
                  <div className="mt-3 space-y-4">
                    <TextField
                      id="new-job-warmpool"
                      label="Warm pool (optional)"
                      value={warmpool}
                      onChange={setWarmpool}
                      placeholder="SandboxTemplate override"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={debug}
                        onChange={(e) => setDebug(e.target.checked)}
                      />
                      Debug (keep the sandbox alive after completion)
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Dirty-close confirm (in-app Modal per repo convention — cf.
          CancelRunButton). Rendered after the main Modal so it stacks on top. */}
      <Modal
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        title="Discard prompt?"
        footer={
          <>
            <ActionButton onClick={() => setConfirmDiscardOpen(false)} variant="neutral">
              Keep editing
            </ActionButton>
            <ActionButton
              onClick={() => {
                setConfirmDiscardOpen(false);
                onClose();
              }}
              variant="danger"
            >
              Discard prompt
            </ActionButton>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Closing this dialog discards the prompt you&apos;ve typed — it isn&apos;t saved anywhere.
        </p>
      </Modal>
    </>
  );
}
